import axios from 'axios';

// VITE_BACKEND_URL is baked in at build time by Vite.
// For same-domain Emergent deployments (frontend and backend on the same host)
// the platform must set VITE_BACKEND_URL to the public app URL in Project Settings → Environment Variables.
// If the variable is absent or empty the fallback to window.location.origin ensures API calls
// always resolve relative to whatever domain the app is actually served from.
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    register: (email, password, first_name, last_name) => api.post('/auth/register', { email, password, first_name, last_name }),
    requestMagicLink: (email) => api.post('/auth/magic-link', { email }),
    verifyMagicLink: (token) => api.post('/auth/verify', { token }),
    getMe: () => api.get('/auth/me'),
    getAuthStatus: () => api.get('/auth/me/auth-status'),
    setPassword: (password) => api.post('/auth/set-password', { password }),
    changePassword: (currentPassword, newPassword) => 
        api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
    updateProfile: (firstName, lastName) => 
        api.patch('/auth/profile', { first_name: firstName, last_name: lastName }),
    // Password reset endpoints
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    verifyResetToken: (token) => api.get(`/auth/reset-password/verify/${token}`),
    resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
    // OAuth endpoints
    googleCallback: (sessionId) => api.post('/auth/oauth/google/callback', { session_id: sessionId }),
    appleCallback: (idToken, code, userData) => api.post('/auth/oauth/apple/callback', {
        id_token: idToken,
        code,
        user: userData || null,
    }),
    getOAuthProviders: () => api.get('/auth/oauth/providers'),
    // Remember device endpoints
    createRememberToken: () => api.post('/auth/remember-device'),
    verifyRememberToken: (rememberToken) => api.post('/auth/verify-device', { remember_token: rememberToken }),
    revokeRememberToken: (rememberToken) => api.delete('/auth/remember-device', { data: { remember_token: rememberToken } }),
    revokeAllRememberTokens: () => api.delete('/auth/remember-device/all'),
};

// Videos API
export const videosAPI = {
    getAll: (type, publishedOnly = true) => 
        api.get('/videos', { params: { type, published_only: publishedOnly } }),
    getById: (id) => api.get(`/videos/${id}`),
    checkAccess: (id) => api.get(`/videos/${id}/access`),
    getEmbed: (id) => api.get(`/videos/${id}/embed`),
};

// Courses API
export const coursesAPI = {
    getAll: (publishedOnly = true) => 
        api.get('/courses', { params: { published_only: publishedOnly } }),
    getById: (id) => api.get(`/courses/${id}`),
};

// Workshops API
export const workshopsAPI = {
    getAll: (publishedOnly = true) => 
        api.get('/workshops', { params: { published_only: publishedOnly } }),
};

// Sculptures API
export const sculpturesAPI = {
    getAll: () => api.get('/sculptures'),
};

// My Library API
export const libraryAPI = {
    getMyLibrary: () => api.get('/my-library'),
    getMyEntitlements: () => api.get('/my-library/entitlements'),
};

// Featured API
export const featuredAPI = {
    getFeatured: () => api.get('/featured'),
};

// Payment API
export const paymentAPI = {
    createCheckout: (videoId) => api.post('/checkout/create', { 
        video_id: videoId,
        origin_url: window.location.origin 
    }),
    getCheckoutStatus: (sessionId) => api.get(`/checkout/status/${sessionId}`),
    getMyPurchases: () => api.get('/my-purchases'),
};

// Progress API
export const progressAPI = {
    updateProgress: (videoId, progressSeconds, durationSeconds) => 
        api.post(`/progress/videos/${videoId}`, { 
            progress_seconds: progressSeconds, 
            duration_seconds: durationSeconds 
        }),
    getProgress: (videoId) => api.get(`/progress/videos/${videoId}`),
    getContinueWatching: () => api.get('/progress/continue-watching'),
    getStats: () => api.get('/progress/stats'),
    resetProgress: (videoId) => api.delete(`/progress/videos/${videoId}`),
};

// Admin API
export const adminAPI = {
    getVideos: () => api.get('/admin/videos'),
    updateVideo: (id, data) => api.patch(`/admin/videos/${id}`, data),
    getUsers: (skip = 0, limit = 100, search = null) => 
        api.get('/admin/users', { params: { skip, limit, ...(search && { search }) } }),
    getUserEntitlements: (userId) => api.get(`/admin/users/${userId}/entitlements`),
    addUserEntitlement: (userId, videoId) => 
        api.post(`/admin/users/${userId}/entitlements`, { video_id: videoId }),
    removeUserEntitlement: (userId, videoId) => 
        api.delete(`/admin/users/${userId}/entitlements/${videoId}`),
    grantAllAccess: (userId) => api.post(`/admin/users/${userId}/grant-all`),
    getStats: () => api.get('/admin/stats'),
    getPurchases: (skip = 0, limit = 50) => api.get('/admin/purchases', { params: { skip, limit } }),
    stripeSync: () => api.post('/admin/stripe/sync'),
    runMigration: () => api.post('/admin/migrate'),
};

// Support API (secure contact form)
export const supportAPI = {
    getFormToken: () => api.post('/support/form-token'),
    submitForm: (data) => api.post('/support/submit', data),
    getCategories: () => api.get('/support/categories'),
};

// SEO API
export const seoAPI = {
    getMethods: () => api.get('/seo/methods'),
    getMethod: (slug) => api.get(`/seo/methods/${slug}`),
    getOrganizationSchema: () => api.get('/seo/schema/organization'),
};

// Leads API
export const leadsAPI = {
    capture: (email) => api.post('/leads/capture', { email }),
};

export default api;
